import { useState } from 'react';
import type { ChatEvent, LiveSummary, Platform } from '@multimod/shared';
import type { ChatFeedStore } from '@/features/chat-feed/store';
import { useMe } from '@/features/auth/hooks';

/**
 * Painel de injeção dev-only para validar UI/UX sem live real.
 *
 * Só renderiza quando `me.devMockActions === true` (gate duplo: backend só
 * expõe a flag quando APP_ENV === 'development' + DEV_MOCK_ACTIONS=true).
 *
 * Não toca WS. Pusha eventos direto no `ChatFeedStore`.
 * As ações de moderação seguem para `/moderation/*` que já são mockadas no
 * worker — fluxo end-to-end fica realista exceto pela origem das msgs.
 */
interface Props {
  store: ChatFeedStore | null;
  selectedLive: LiveSummary | null;
  onAttachMockLive: (live: LiveSummary, attached: LiveSummary[]) => void;
}

function mockMessage(args: {
  platform: Platform;
  liveChatId: string;
  text: string;
  displayName: string;
  externalUserId: string;
  isModerator?: boolean;
  isBroadcaster?: boolean;
  timestampOffsetMs?: number;
}): ChatEvent {
  return {
    type: 'message',
    platform: args.platform,
    liveChatId: args.liveChatId,
    externalId: `mock-msg-${crypto.randomUUID()}`,
    text: args.text,
    timestamp: Date.now() - (args.timestampOffsetMs ?? 0),
    author: {
      platform: args.platform,
      externalId: args.externalUserId,
      displayName: args.displayName,
    },
    isModerator: args.isModerator ?? false,
    isBroadcaster: args.isBroadcaster ?? false,
  };
}

const TWITCH_LIVE: LiveSummary = {
  platform: 'twitch',
  liveChatId: '__mm-mock:twitch-gaules',
  login: 'gauleslive',
  displayName: 'gauleslive',
  title: 'FURIA vs MOUZ — Major Berlin 2026',
  gameName: 'Counter-Strike 2',
  viewerCount: 12_413,
  thumbnailUrl: 'https://example.com/thumb-twitch.jpg',
  startedAt: Math.floor(Date.now() / 1000) - 3600,
};

const YOUTUBE_LIVE: LiveSummary = {
  platform: 'youtube',
  liveChatId: '__mm-mock:youtube-gaules',
  login: 'UCxxxxxxxxxx',
  displayName: 'Gaules',
  title: 'FURIA vs MOUZ — Major Berlin 2026',
  gameName: null,
  viewerCount: 3_119,
  thumbnailUrl: 'https://example.com/thumb-yt.jpg',
  startedAt: Math.floor(Date.now() / 1000) - 3600,
};

const SAMPLE_NICKS_TWITCH = [
  { nick: 'felipe_n4ito', extId: 'tw-1' },
  { nick: 'marcellobacelar', extId: 'tw-2', mod: true },
  { nick: 'nina_chibi', extId: 'tw-3' },
  { nick: 'th3_silent_one', extId: 'tw-4' },
  { nick: 'duckcs_pov', extId: 'tw-5' },
  { nick: 'k1ng_donut', extId: 'tw-6' },
  { nick: 'paulazinha_furiosa', extId: 'tw-7' },
  { nick: 'brunolpls', extId: 'tw-8' },
  { nick: 'vagnerlove89', extId: 'tw-9' },
];

const SAMPLE_NICKS_YT = [
  { nick: 'Rodrigo Almeida', extId: 'yt-1' },
  { nick: 'canal_imperador', extId: 'yt-2', mod: true },
  { nick: 'guigorillaz', extId: 'yt-3' },
  { nick: 'helena_v_silva', extId: 'yt-4' },
  { nick: 'joao_cs_legends', extId: 'yt-5' },
  { nick: 'bruno santos', extId: 'yt-6' },
  { nick: 'vitorhuugo_', extId: 'yt-7' },
  { nick: 'manudani_', extId: 'yt-8' },
  { nick: 'eduardopianista', extId: 'yt-9' },
];

const SAMPLE_TEXTS = [
  'que round é esse??',
  'VAMO FURIA',
  'CLUTCH IRRAL PEAKHEAD DIABO',
  'galera, sem spoiler do playoff por favor',
  'esse Vinis é monstro hein',
  'o yuurih hj tá em outro patamar pqp 🔥',
  'gaules canta a vinheta canta a vinheta',
  'faltam 2 rounds pra fechar o mapa',
  'KSCERATO só hit cabeça',
  'smoke do CT não tá fechando direito hj',
  'queria ver pelo celular mas o yt tá engasgando',
];

// URL fake explícita (example.invalid) evita falso positivo em scans de segurança
// que checam shorteners reais; ainda valida a UI de detecção/destaque de link.
const SPAM_TEXT = 'G4LERA olha esse drop oficial do imperador 👉 example.invalid/furia-claim só os primeiros 50 ganham skin AWP dragon lore';

export function DevMockPanel({ store, selectedLive, onAttachMockLive }: Props) {
  const me = useMe();
  const [open, setOpen] = useState(false);

  if (!me.data?.devMockActions) return null;

  const isAttached = selectedLive?.liveChatId === TWITCH_LIVE.liveChatId;

  function attach() {
    onAttachMockLive(TWITCH_LIVE, [YOUTUBE_LIVE]);
  }

  function pushOne(platform: Platform, opts: { spam?: boolean; mod?: boolean; broadcaster?: boolean } = {}) {
    if (!store) return;
    const pool = platform === 'twitch' ? SAMPLE_NICKS_TWITCH : SAMPLE_NICKS_YT;
    const liveChatId = platform === 'twitch' ? TWITCH_LIVE.liveChatId : YOUTUBE_LIVE.liveChatId;
    const author = opts.mod
      ? pool.find((n) => n.mod) ?? pool[0]!
      : pool[Math.floor(Math.random() * pool.length)]!;
    const text = opts.spam
      ? SPAM_TEXT
      : SAMPLE_TEXTS[Math.floor(Math.random() * SAMPLE_TEXTS.length)]!;
    store.getState().push(
      mockMessage({
        platform,
        liveChatId,
        text,
        displayName: opts.broadcaster ? (platform === 'twitch' ? 'gauleslive' : 'Gaules') : author.nick,
        externalUserId: opts.broadcaster ? `${platform}-broadcaster` : author.extId,
        isModerator: opts.mod ?? false,
        isBroadcaster: opts.broadcaster ?? false,
      }),
    );
  }

  function pushBurst(count: number) {
    if (!store) return;
    for (let i = 0; i < count; i++) {
      const platform: Platform = Math.random() < 0.6 ? 'twitch' : 'youtube';
      pushOne(platform);
    }
  }

  function pushFirstTime() {
    if (!store) return;
    store.getState().push(
      mockMessage({
        platform: 'twitch',
        liveChatId: TWITCH_LIVE.liveChatId,
        text: 'oi pessoal, primeira vez aqui — indicação do imperador',
        displayName: 'lucasdoescosso',
        externalUserId: 'tw-first-time',
      }),
    );
  }

  function pushSystemEvent(kind: 'sub' | 'superchat' | 'raid') {
    if (!store) return;
    const platform: Platform = kind === 'superchat' ? 'youtube' : 'twitch';
    const liveChatId = platform === 'twitch' ? TWITCH_LIVE.liveChatId : YOUTUBE_LIVE.liveChatId;
    store.getState().push({
      type: 'platform',
      platform,
      liveChatId,
      kind,
      payload:
        kind === 'sub'
          ? { user: 'vagnerlove89', months: 3, message: 'bora pro hexa furia' }
          : kind === 'superchat'
            ? { user: 'Diego Comerlato', amount: 25_00, currency: 'BRL', message: 'bora hexa, força gauleslive' }
            : { from: 'casimiro', viewers: 2400 },
      timestamp: Date.now(),
    });
  }

  function reset() {
    store?.getState().reset();
  }

  return (
    <div
      data-dev-mock-panel
      className="fixed bottom-4 left-4 z-[100] bg-bg-popover border border-border-strong rounded-lg shadow-popover text-xs"
      style={{ width: open ? 320 : 'auto' }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-3 py-2 flex items-center gap-2 text-fg-muted hover:text-fg"
      >
        <span aria-hidden="true">🎭</span>
        <span className="font-mono uppercase tracking-wide">dev mock</span>
        <span className="ml-auto text-fg-subtle">{open ? '▼' : '▲'}</span>
      </button>
      {open && (
        <div className="border-t border-border p-3 flex flex-col gap-2">
          <button
            type="button"
            onClick={attach}
            disabled={isAttached}
            className="h-8 px-3 rounded-md bg-primary text-primary-fg font-medium disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isAttached ? '✓ Live mock atachada' : 'Atachar live mock (Twitch + YT)'}
          </button>

          <div className="text-fg-subtle uppercase tracking-wide font-mono text-2xs mt-1">Mensagens</div>
          <div className="grid grid-cols-2 gap-1.5">
            <button type="button" onClick={() => pushOne('twitch')} className="h-7 px-2 rounded-sm bg-bg-muted hover:bg-bg-hover text-fg">+1 Twitch</button>
            <button type="button" onClick={() => pushOne('youtube')} className="h-7 px-2 rounded-sm bg-bg-muted hover:bg-bg-hover text-fg">+1 YouTube</button>
            <button type="button" onClick={() => pushBurst(10)} className="h-7 px-2 rounded-sm bg-bg-muted hover:bg-bg-hover text-fg">+10 mistas</button>
            <button type="button" onClick={() => pushBurst(50)} className="h-7 px-2 rounded-sm bg-bg-muted hover:bg-bg-hover text-fg">+50 mistas</button>
          </div>

          <div className="text-fg-subtle uppercase tracking-wide font-mono text-2xs mt-1">Estados especiais</div>
          <div className="grid grid-cols-2 gap-1.5">
            <button type="button" onClick={() => pushOne('twitch', { spam: true })} className="h-7 px-2 rounded-sm bg-warning-soft text-warning hover:bg-warning/20">spam Twitch</button>
            <button type="button" onClick={() => pushOne('youtube', { spam: true })} className="h-7 px-2 rounded-sm bg-warning-soft text-warning hover:bg-warning/20">spam YouTube</button>
            <button type="button" onClick={pushFirstTime} className="h-7 px-2 rounded-sm bg-info-soft text-info hover:bg-info/20">primeira vez</button>
            <button type="button" onClick={() => pushOne('twitch', { mod: true })} className="h-7 px-2 rounded-sm bg-success/15 text-success hover:bg-success/25">msg de mod</button>
            <button type="button" onClick={() => pushOne('twitch', { broadcaster: true })} className="h-7 px-2 rounded-sm bg-primary-soft text-primary hover:bg-primary/20">msg broadcaster</button>
          </div>

          <div className="text-fg-subtle uppercase tracking-wide font-mono text-2xs mt-1">Eventos sistema</div>
          <div className="grid grid-cols-3 gap-1.5">
            <button type="button" onClick={() => pushSystemEvent('sub')} className="h-7 px-2 rounded-sm bg-platform-twitch-soft text-platform-twitch hover:opacity-80">sub</button>
            <button type="button" onClick={() => pushSystemEvent('superchat')} className="h-7 px-2 rounded-sm bg-platform-youtube-soft text-platform-youtube hover:opacity-80">superchat</button>
            <button type="button" onClick={() => pushSystemEvent('raid')} className="h-7 px-2 rounded-sm bg-platform-twitch-soft text-platform-twitch hover:opacity-80">raid</button>
          </div>

          <button type="button" onClick={reset} className="h-7 px-2 rounded-sm bg-destructive-soft text-destructive hover:bg-destructive/25 mt-1">limpar feed</button>
        </div>
      )}
    </div>
  );
}
